import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { useToast } from "@/components/ui/use-toast"
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel"
import { MapPin, Video, Globe, Users } from "lucide-react"
import { arrayToString, stringToArray } from "./catalog-form-schema"
import { Separator } from "@/components/ui/separator"
import { 
  DEFAULT_CANCELLATION_POLICY,
  DEFAULT_CANCELLATION_TERMS,
  CANCELLATION_POLICIES
} from "@/constants/forms"
import { useOnboardingConfig } from "@/components/onboarding/OnboardingSteps"

type Opportunity = any

interface ReviewAndPublishProps {
  opportunity: Opportunity | null
  catalogData: any
  onBack: () => void
  onComplete: () => void
  session: any
  profile: any
}

const DEFAULT_IMAGE = "https://images.unsplash.com/photo-1488590528505-98d2b5aba04b"

export function ReviewAndPublish({
  opportunity,
  catalogData,
  onBack,
  onComplete,
  session,
  profile,
}: ReviewAndPublishProps) {
  const [isPublishing, setIsPublishing] = useState(false)
  const { toast } = useToast()
  const { updateConfig } = useOnboardingConfig();

  console.error(catalogData)
  const getFormatIcon = (format: string) => {
    switch (format.toLowerCase()) {
      case 'in-person':
        return <MapPin className="h-4 w-4" />
      case 'virtual':
        return <Video className="h-4 w-4" />
      case 'hybrid':
        return <Globe className="h-4 w-4" />
      default:
        return <Users className="h-4 w-4" />
    }
  }

  const handlePublish = async () => {
    try {
      setIsPublishing(true);

      if (!session?.access_token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(
        `${import.meta.env.VITE_CC_API_URL}/v1/opportunities/catalog`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            opportunity_id: opportunity.id,
            title: catalogData.title,
            description: catalogData.description,
            prerequisites: Array.isArray(catalogData.prerequisites) 
              ? catalogData.prerequisites 
              : stringToArray(catalogData.prerequisites),
            timeline: Array.isArray(catalogData.timeline)
              ? catalogData.timeline
              : stringToArray(catalogData.timeline),
            event_cost: catalogData.eventCost,
            min_volunteers: catalogData.minVolunteers,
            max_volunteers: catalogData.maxVolunteers,
            host_included: catalogData.host_included,
            format: catalogData.format,
            duration: catalogData.duration,
            format_details: catalogData.format_details || 'To Be Filled',
            whats_included: catalogData.whats_included,
            faqs: catalogData.faqs,
            waiver_forms: catalogData.waiver_forms,
            terms_and_conditions: catalogData.terms_and_conditions,
            cancellation_policy: catalogData.cancellation_policy || DEFAULT_CANCELLATION_POLICY,
            cancellation_terms: catalogData.cancellation_terms || DEFAULT_CANCELLATION_TERMS,
            volunteer_output: catalogData.volunteerOutput,
            location: catalogData.location,
            images: catalogData.images || []
          }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to create catalog entry');
      } else {
        console.log("Updating onboarding config")
        updateConfig({corporate_event: true})
      }

      const result = await response.json();
      
      toast({
        title: "Success!",
        description: "Your opportunity has been published to the catalog.",
      });

      onComplete()
    } catch (error) {
      console.error('Error publishing opportunity:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to publish opportunity. Please try again.",
      });
    } finally {
      setIsPublishing(false);
    }
  }

  const renderList = (items: string | string[]) => {
    if (typeof items === "string") {
      items = stringToArray(items)
    }
    return items.map((item: string, index: number) => (
      <li key={index}>{item}</li>
    ))
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium mb-2">Review Your Catalog Entry</h3>
        <p className="text-sm text-gray-500">
          Please review the information below before publishing your catalog entry.
        </p>
      </div>

      <Card>
        <CardContent className="p-6">
          {/* Images Section */}
          <div className="space-y-6">
            <div>
              <h4 className="text-sm font-medium text-gray-500 mb-4">Images</h4>
              {catalogData?.images && catalogData.images.length > 0 ? (
                <Carousel className="w-full max-w-xs mx-auto">
                  <CarouselContent>
                    {catalogData.images.map((image: string, index: number) => (
                      <CarouselItem key={index}>
                        <img
                          src={image}
                          alt={`Preview ${index + 1}`}
                          className="w-full h-48 object-cover rounded-lg"
                        />
                      </CarouselItem>
                    ))}
                  </CarouselContent>
                  <CarouselPrevious />
                  <CarouselNext />
                </Carousel>
              ) : (
                <p className="text-sm text-gray-400">No images uploaded</p>
              )}
            </div>

            <Separator />

            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="text-sm font-medium text-gray-500 mb-2">Title</h4>
                <p className="text-lg">{catalogData?.title}</p>
              </div>

              <div>
                <h4 className="text-sm font-medium text-gray-500 mb-2">Format</h4>
                <div className="flex items-center gap-2">
                  {getFormatIcon(catalogData?.format)}
                  <span>{catalogData?.format}</span>
                </div>
                {catalogData?.format_details && (
                  <p className="text-sm text-gray-600 mt-2">
                    Details: {catalogData.format_details}
                  </p>
                )}
              </div>

              <div className="col-span-2">
                <h4 className="text-sm font-medium text-gray-500 mb-2">Description</h4>
                <p className="text-gray-700">{catalogData?.description}</p>
              </div>
            </div>

            <Separator />

            {/* Location and Logistics */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {catalogData?.location && (
                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-2 flex items-center gap-2">
                    <MapPin className="h-4 w-4" />
                    Location
                  </h4>
                  <p>{catalogData.location}</p>
                </div>
              )}

              <div>
                <h4 className="text-sm font-medium text-gray-500 mb-2">Event Cost</h4>
                <p>${catalogData?.eventCost}</p>
              </div>

              <div>
                <h4 className="text-sm font-medium text-gray-500 mb-2">Minimum Volunteers</h4>
                <p>{catalogData?.minVolunteers} volunteers</p>
              </div>

              <div>
                <h4 className="text-sm font-medium text-gray-500 mb-2">Maximum Volunteers</h4>
                <p>{catalogData?.maxVolunteers} volunteers</p>
              </div>

              <div>
                <h4 className="text-sm font-medium text-gray-500 mb-2">Host Included</h4>
                <p>{catalogData?.host_included ? "Yes" : "No"}</p>
              </div>
            </div>

            <Separator />

            {/* Causes and SDGs */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {catalogData?.causes && (
                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-2">Causes</h4>
                  <div className="flex flex-wrap gap-2">
                    {catalogData.causes.map((cause: string, index: number) => (
                      <span key={index} className="px-2 py-1 bg-blue-50 text-blue-700 rounded-full text-sm">
                        {cause}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {catalogData?.sdg_alignment && catalogData.sdg_alignment.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-2">SDGs</h4>
                  <div className="flex flex-wrap gap-2">
                    {catalogData.sdg_alignment.map((sdg: string, index: number) => (
                      <span key={index} className="px-2 py-1 bg-purple-50 text-purple-700 rounded-full text-sm">
                        {sdg}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <Separator />

            {/* Timeline and Prerequisites */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {catalogData?.timeline && (
                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-2">Timeline</h4>
                  <ul className="list-disc pl-5 space-y-1">
                    {renderList(catalogData.timeline)}
                  </ul>
                </div>
              )}

              {catalogData?.prerequisites && (
                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-2">Prerequisites</h4>
                  <ul className="list-disc pl-5 space-y-1">
                    {renderList(catalogData.prerequisites)}
                  </ul>
                </div>
              )}
            </div>

            <Separator />

            {/* Additional Information */}
            <div className="space-y-4">
              <div>
                <h4 className="text-sm font-medium text-gray-500 mb-2">Volunteer Output</h4>
                <p>{catalogData?.volunteerOutput}</p>
              </div>

              {catalogData?.whatsIncluded && (
                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-2">What's Included</h4>
                  <p>{catalogData.whatsIncluded}</p>
                </div>
              )}

              <Separator className="my-4" />
              <div className="space-y-4">
                <h4 className="text-sm font-medium text-gray-500 mb-2">Cancellation Policy</h4>
                <div>
                  <h5 className="text-sm font-medium text-gray-500 mb-1">Policy</h5>
                  <p>{CANCELLATION_POLICIES.find(p => p.value === catalogData?.cancellation_policy)?.label || "Non-Refundable"}</p>
                </div>
                <div>
                  <h5 className="text-sm font-medium text-gray-500 mb-1">Terms</h5>
                  <p>{catalogData?.cancellation_terms || "No refund available for this event"}</p>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-between">
        <Button variant="outline" onClick={onBack}>
          Back
        </Button>
        <Button onClick={handlePublish} disabled={isPublishing}>
          {isPublishing ? "Publishing..." : "Publish"}
        </Button>
      </div>
    </div>
  )
}